import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight,
  ArrowLeft,
  Globe,
  Clock,
  Mail,
  User,
  MessageCircle,
  Smile,
  MessageSquare,
  Phone,
  Smartphone,
  Check,
  Rocket,
  Plus,
  Trash2,
  ShieldCheck,
  Pencil,
  Home,
  Sparkles,
  Loader2,
  Building2,
  Info,
  Briefcase,
  Hammer,
  DollarSign,
  MapPin,
  Siren,
  AlertTriangle,
  Wrench,
  Palette,
  Calendar,
  Users,
  Scale,
  Target,
  FileText,
  Gavel,
  Sun,
} from "lucide-react";
import Sidebar from "@/components/Sidebar";
import Cookies from "js-cookie";
import { getClientAuthHeaders, getClientAuthToken } from "@/lib/apiAuth";
import { trackSettingsCompletedIfAuthenticated } from "@/lib/leadEvents";
import { useGhlIntegrationActive } from "@/hooks/useGhlIntegrationActive";
import {
  cloneRenovationDefaults,
  RENOVATION_BOT_SETTINGS_DEFAULTS,
  CANONICAL_APPOINTMENT_TYPE_KEYS,
  CANONICAL_DISQUAL_ENUMS,
  CANONICAL_TONE_STYLES,
} from "./renovationSettingsDefaults";
import { normalizeRenovationSettings } from "./normalizeRenovationSettings";
import {
  APPOINTMENT_TYPE_LABELS,
  BookingGateKeysDisplay,
  EmergencySafetyActionsEditor,
  PublicHolidaysEditor,
  TeamCalendarsEditor,
} from "./renovationSettingsEditors";
import { QuestionnaireEditor } from "@/pages/BotSettings/shared/QuestionnaireEditor";
import { SkipWeekdayConditionsEditor } from "@/pages/BotSettings/shared/SkipWeekdayConditionsEditor";

// Local Storage Key (separate from real estate to avoid collisions)
const STORAGE_KEY = "ai_renovation_onboarding_data_v1";

/** UI-only visibility flags; data still loads/saves via formData. */
const RENOVATION_UI_HIDDEN = {
  outOfHoursMessage_6_3: false,
  globalObjective_6_1: false,
  knownUserHandling_6_1: false,
  financing_8_4: false,
  ballparkPricing_8_5: false,
  supportRoles_10_1: false,
  otherServices_3_3: true,
};

// Canonical defaults — ADAPTATION_SETTINGS_JSON.md §1
const initialFormData = cloneRenovationDefaults();

// URL validation helper
const validateUrl = (url) => {
  if (!url) return false;
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
};

// ============ Shared form primitives ============

const ProgressBar = ({ currentStep, totalSteps }) => {
  const progress = (currentStep / totalSteps) * 100;
  return (
    <div className="w-full" data-testid="progress-bar">
      <div className="flex justify-between items-center mb-2">
        <span className="font-mono text-[#00D1FF] text-sm">
          Step {currentStep} of {totalSteps}
        </span>
        <span className="font-mono text-[#9CA3AF] text-sm">
          {Math.round(progress)}% Complete
        </span>
      </div>
      <div className="h-2 bg-white/10 rounded-full overflow-hidden">
        <div
          className="h-full bg-gradient-to-r from-[#00D1FF] to-[#00D1FF]/70 rounded-full transition-all duration-500 ease-out"
          style={{ width: `${progress}%` }}
        />
      </div>
    </div>
  );
};

const StepIndicator = ({
  steps,
  currentStep,
  onStepClick,
  hasExistingSettings,
  isSaving,
}) => (
  <div
    className="hidden lg:flex items-center justify-center gap-2 mb-8 flex-wrap"
    data-testid="step-indicator"
  >
    {steps.map((step, index) => {
      const stepNum = index + 1;
      const isActive = stepNum === currentStep;
      const isCompleted = stepNum < currentStep;
      const isForwardBlocked = !hasExistingSettings && stepNum > currentStep;
      const isDisabled = isSaving || isForwardBlocked;

      return (
        <React.Fragment key={stepNum}>
          <button
            type="button"
            onClick={() => onStepClick(stepNum)}
            disabled={isDisabled}
            data-testid={`step-indicator-${stepNum}`}
            className={`flex items-center gap-2 bg-transparent border-0 p-0 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#00D1FF] ${
              isDisabled ? "cursor-not-allowed opacity-50" : "cursor-pointer"
            }`}
            aria-current={isActive ? "step" : undefined}
            aria-label={`Go to step ${stepNum}: ${step.shortTitle}`}
          >
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center font-mono text-xs transition-all duration-300 ${
                isActive
                  ? "bg-[#00D1FF] text-[#0F1115]"
                  : isCompleted
                  ? "bg-[#00D1FF]/20 text-[#00D1FF] border border-[#00D1FF]/50"
                  : "bg-white/5 text-[#9CA3AF] border border-white/10 hover:border-[#00D1FF]/50"
              }`}
            >
              {isCompleted ? <Check className="w-4 h-4" /> : stepNum}
            </div>
            <span
              className={`text-xs font-body hidden xl:block ${
                isActive ? "text-[#F3F4F6]" : "text-[#9CA3AF]"
              }`}
            >
              {step.shortTitle}
            </span>
          </button>
          {index < steps.length - 1 && (
            <div
              className={`w-4 h-0.5 ${isCompleted ? "bg-[#00D1FF]/50" : "bg-white/10"}`}
            />
          )}
        </React.Fragment>
      );
    })}
  </div>
);

const FormInput = ({ label, required, error, icon: Icon, sublabel, ...props }) => (
  <div className="space-y-2">
    <label className="flex items-center gap-2 text-[#F3F4F6] text-sm font-body">
      {Icon && <Icon className="w-4 h-4 text-[#00D1FF]" />}
      {label}
      {required && <span className="text-[#00D1FF]">*</span>}
    </label>
    {sublabel && <p className="text-[#9CA3AF] text-xs font-body -mt-1 ml-6">{sublabel}</p>}
    <input
      {...props}
      className={`w-full bg-white/5 border rounded-lg px-4 py-3 text-[#F3F4F6] placeholder-[#9CA3AF]/50 focus:outline-none transition-all font-body ${
        error
          ? "border-red-500/70 focus:border-red-500 focus:ring-1 focus:ring-red-500/50"
          : "border-white/10 focus:border-[#00D1FF]/50 focus:ring-1 focus:ring-[#00D1FF]/50"
      }`}
    />
    {error && <p className="text-red-400 text-xs font-body">{error}</p>}
  </div>
);

const FormTextarea = ({ label, required, error, icon: Icon, sublabel, rows = 3, ...props }) => (
  <div className="space-y-2">
    <label className="flex items-center gap-2 text-[#F3F4F6] text-sm font-body">
      {Icon && <Icon className="w-4 h-4 text-[#00D1FF]" />}
      {label}
      {required && <span className="text-[#00D1FF]">*</span>}
    </label>
    {sublabel && <p className="text-[#9CA3AF] text-xs font-body -mt-1 ml-6">{sublabel}</p>}
    <textarea
      rows={rows}
      {...props}
      className={`w-full bg-white/5 border rounded-lg px-4 py-3 text-[#F3F4F6] placeholder-[#9CA3AF]/50 focus:outline-none transition-all font-body resize-y ${
        error
          ? "border-red-500/70 focus:border-red-500 focus:ring-1 focus:ring-red-500/50"
          : "border-white/10 focus:border-[#00D1FF]/50 focus:ring-1 focus:ring-[#00D1FF]/50"
      }`}
    />
    {error && <p className="text-red-400 text-xs font-body">{error}</p>}
  </div>
);

const ToggleSwitch = ({ label, description, checked, onChange, icon: Icon }) => (
  <div className="flex items-start justify-between gap-4 p-4 rounded-lg bg-white/5 border border-white/10 hover:border-white/20 transition-all">
    <div className="flex items-start gap-3 flex-1">
      {Icon && <Icon className="w-5 h-5 text-[#00D1FF] mt-0.5" />}
      <div className="flex-1">
        <span className="text-[#F3F4F6] text-sm font-body">{label}</span>
        {description && (
          <p className="text-[#9CA3AF] text-xs font-body mt-1">{description}</p>
        )}
      </div>
    </div>
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className={`relative w-12 h-6 rounded-full transition-colors duration-300 flex-shrink-0 ${
        checked ? "bg-[#00D1FF]" : "bg-white/20"
      }`}
      aria-pressed={checked}
    >
      <div
        className={`absolute top-1 w-4 h-4 rounded-full bg-white shadow transition-transform duration-300 ${
          checked ? "translate-x-7" : "translate-x-1"
        }`}
      />
    </button>
  </div>
);

const RadioGroup = ({ label, required, options, value, onChange, error, icon: Icon }) => (
  <div className="space-y-3">
    <label className="flex items-center gap-2 text-[#F3F4F6] text-sm font-body">
      {Icon && <Icon className="w-4 h-4 text-[#00D1FF]" />}
      {label}
      {required && <span className="text-[#00D1FF]">*</span>}
    </label>
    <div className="space-y-2">
      {options.map((option) => (
        <label
          key={option.value === "" ? "__none__" : option.value}
          className={`flex items-start gap-3 p-4 rounded-lg border cursor-pointer transition-all ${
            value === option.value
              ? "bg-[#00D1FF]/10 border-[#00D1FF]/50"
              : "bg-white/5 border-white/10 hover:border-white/20"
          }`}
        >
          <input
            type="radio"
            name={label}
            value={option.value}
            checked={value === option.value}
            onChange={(e) => onChange(e.target.value)}
            className="sr-only"
          />
          <div
            className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${
              value === option.value ? "border-[#00D1FF] bg-[#00D1FF]" : "border-white/30"
            }`}
          >
            {value === option.value && <div className="w-2 h-2 rounded-full bg-[#0F1115]" />}
          </div>
          <div className="flex flex-col">
            <span className="text-[#F3F4F6] text-sm font-body">{option.label}</span>
            {option.description && (
              <span className="text-[#9CA3AF] text-xs font-body mt-1">{option.description}</span>
            )}
          </div>
        </label>
      ))}
    </div>
    {error && <p className="text-red-400 text-xs font-body">{error}</p>}
  </div>
);

// Tri-state Yes / No / Sometimes picker used for services_offered
const TriStatePicker = ({ label, value, onChange }) => {
  const opts = [
    { v: "true", label: "Yes", cls: "bg-emerald-500/15 border-emerald-400/50 text-emerald-300" },
    { v: "sometimes", label: "Sometimes", cls: "bg-amber-500/15 border-amber-400/50 text-amber-300" },
    { v: "false", label: "No", cls: "bg-red-500/15 border-red-400/50 text-red-300" },
  ];
  return (
    <div className="flex items-center justify-between gap-3 p-3 rounded-lg bg-white/5 border border-white/10">
      <span className="text-[#F3F4F6] text-sm font-body flex-1">{label}</span>
      <div className="flex gap-1.5 flex-shrink-0">
        {opts.map((o) => {
          const active = (value || "false") === o.v;
          return (
            <button
              key={o.v}
              type="button"
              onClick={() => onChange(o.v)}
              className={`px-3 py-1.5 rounded-md text-xs font-body border transition-all ${
                active
                  ? o.cls
                  : "bg-white/5 border-white/10 text-[#9CA3AF] hover:border-white/20"
              }`}
            >
              {o.label}
            </button>
          );
        })}
      </div>
    </div>
  );
};

// Dynamic string list (rows with trash buttons + add button)
const StringListEditor = ({ label, sublabel, values = [], onChange, placeholder, icon: Icon }) => (
  <div className="space-y-2">
    {label && (
      <label className="flex items-center gap-2 text-[#F3F4F6] text-sm font-body">
        {Icon && <Icon className="w-4 h-4 text-[#00D1FF]" />}
        {label}
      </label>
    )}
    {sublabel && <p className="text-[#9CA3AF] text-xs font-body -mt-1 ml-6">{sublabel}</p>}
    <div className="space-y-2">
      {values.map((item, idx) => (
        <div key={idx} className="flex items-center gap-2">
          <input
            type="text"
            value={item}
            onChange={(e) => onChange(values.map((v, i) => (i === idx ? e.target.value : v)))}
            placeholder={placeholder}
            className="flex-1 bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 text-[#F3F4F6] placeholder-[#9CA3AF]/50 focus:outline-none focus:border-[#00D1FF]/50 focus:ring-1 focus:ring-[#00D1FF]/50 transition-all font-body text-sm"
          />
          <button
            type="button"
            onClick={() => onChange(values.filter((_, i) => i !== idx))}
            className="text-[#9CA3AF] hover:text-red-400 transition-colors p-2"
            aria-label="Remove"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      ))}
    </div>
    <button
      type="button"
      onClick={() => onChange([...(values || []), ""])}
      className="flex items-center gap-2 text-[#00D1FF] text-sm font-body hover:text-[#00D1FF]/80 transition-colors py-2"
    >
      <Plus className="w-4 h-4" />
      Add
    </button>
  </div>
);

// Allowed-channels multi-select chip group
const CHANNEL_OPTIONS = [
  { value: "phone_call", label: "Phone Call", icon: Phone },
  { value: "sms", label: "SMS", icon: MessageSquare },
  { value: "whatsapp", label: "WhatsApp", icon: MessageCircle },
  { value: "telegram", label: "Telegram", icon: Smartphone },
];

const AllowedChannelsPicker = ({ value = [], onChange, label = "Allowed Channels" }) => (
  <div className="space-y-1.5">
    <div className="flex items-center gap-2">
      <label className="text-sm font-medium text-[#D1D5DB] font-body">{label}</label>
      <div className="group relative">
        <Info className="w-3.5 h-3.5 text-[#6B7280] cursor-help" />
        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-[#1F2937] text-[#D1D5DB] text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none w-64 z-50 border border-white/10 shadow-xl">
          Channels the AI may use when escalating this scenario to a human.
        </div>
      </div>
    </div>
    <div className="grid grid-cols-2 gap-3">
      {CHANNEL_OPTIONS.map((channel) => {
        const isSelected = value.includes(channel.value);
        const ChannelIcon = channel.icon;
        return (
          <button
            key={channel.value}
            type="button"
            onClick={() => {
              const next = isSelected
                ? value.filter((c) => c !== channel.value)
                : [...value, channel.value];
              onChange(next);
            }}
            className={`flex items-center gap-2.5 px-4 py-3 rounded-xl border transition-all duration-200 text-sm font-body ${
              isSelected
                ? "border-[#D4AF37]/60 bg-[#D4AF37]/10 text-[#D4AF37]"
                : "border-white/10 bg-white/[0.03] text-[#9CA3AF] hover:border-white/20 hover:bg-white/[0.05]"
            }`}
          >
            <ChannelIcon className={`w-4 h-4 ${isSelected ? "text-[#D4AF37]" : "text-[#6B7280]"}`} />
            <span>{channel.label}</span>
            {isSelected && <Check className="w-3.5 h-3.5 ml-auto text-[#D4AF37]" />}
          </button>
        );
      })}
    </div>
  </div>
);

const HiddenUi = ({ hidden, children }) =>
  hidden ? (
    <div className="hidden" aria-hidden="true">
      {children}
    </div>
  ) : (
    children
  );

// Section heading helper
const SectionHeading = ({ title, description }) => (
  <div className="mb-6">
    <h2 className="font-heading font-bold text-2xl md:text-3xl text-[#F3F4F6] mb-2">{title}</h2>
    {description && <p className="text-[#9CA3AF] font-body">{description}</p>}
  </div>
);

const SubHeading = ({ title }) => (
  <h3 className="text-[#F3F4F6] text-sm font-body font-semibold uppercase tracking-wide mt-6 mb-3 pb-2 border-b border-white/10">
    {title}
  </h3>
);

// Day-window editor: null = closed; { start, end } when open
const DayWindowRow = ({ label, dayValue, onChange, showIsOpen = true }) => {
  const isOpen =
    dayValue != null && (showIsOpen ? dayValue.is_open !== false : true);
  const start = dayValue?.start || "";
  const end = dayValue?.end || "";

  const handleToggle = () => {
    if (isOpen) {
      onChange(null);
    } else {
      onChange(
        showIsOpen
          ? { start: "09:00", end: "17:00", is_open: true }
          : { start: "09:00", end: "17:00" }
      );
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-3 p-3 rounded-lg bg-white/5 border border-white/10">
      <span className="text-[#F3F4F6] text-sm font-body w-32 flex-shrink-0">{label}</span>
      {showIsOpen && (
        <button
          type="button"
          onClick={handleToggle}
          className={`relative w-11 h-6 rounded-full transition-colors duration-300 flex-shrink-0 ${
            isOpen ? "bg-[#00D1FF]" : "bg-white/20"
          }`}
          aria-pressed={isOpen}
        >
          <div
            className={`absolute top-1 w-4 h-4 rounded-full bg-white shadow transition-transform duration-300 ${
              isOpen ? "translate-x-6" : "translate-x-1"
            }`}
          />
        </button>
      )}
      <input
        type="time"
        value={start}
        onChange={(e) =>
          onChange(
            showIsOpen
              ? { ...(dayValue || {}), start: e.target.value, is_open: true }
              : { ...(dayValue || {}), start: e.target.value }
          )
        }
        disabled={showIsOpen && !isOpen}
        className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-[#F3F4F6] text-sm font-body focus:outline-none focus:border-[#00D1FF]/50 disabled:opacity-40"
      />
      <span className="text-[#9CA3AF] text-sm">–</span>
      <input
        type="time"
        value={end}
        onChange={(e) =>
          onChange(
            showIsOpen
              ? { ...(dayValue || {}), end: e.target.value, is_open: true }
              : { ...(dayValue || {}), end: e.target.value }
          )
        }
        disabled={showIsOpen && !isOpen}
        className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-[#F3F4F6] text-sm font-body focus:outline-none focus:border-[#00D1FF]/50 disabled:opacity-40"
      />
    </div>
  );
};

const WEEKDAY_ROWS = [
  { key: "monday", label: "Monday" },
  { key: "tuesday", label: "Tuesday" },
  { key: "wednesday", label: "Wednesday" },
  { key: "thursday", label: "Thursday" },
  { key: "friday", label: "Friday" },
  { key: "saturday", label: "Saturday" },
  { key: "sunday", label: "Sunday" },
];

// ==================== STEPS ====================

// ------------------------------------------------------------
// Step 1: Company Profile & Credentials
// ------------------------------------------------------------
const Step1Company = ({ formData, setFormData, errors }) => {
  const kb = formData.knowledge_base;
  const updateKB = (field, value) =>
    setFormData({ ...formData, knowledge_base: { ...kb, [field]: value } });
  const updateCred = (field, value) =>
    setFormData({
      ...formData,
      knowledge_base: {
        ...kb,
        credentials: { ...kb.credentials, [field]: value },
      },
    });
  const updateOffice = (field, value) =>
    setFormData({
      ...formData,
      knowledge_base: {
        ...kb,
        office_location: { ...kb.office_location, [field]: value },
      },
    });

  const socials = kb.social_networks || [];
  const setSocials = (next) => updateKB("social_networks", next);

  return (
    <div className="space-y-6" data-testid="renovation-step-1">
      <SectionHeading
        title="Company Profile & Credentials"
        description="Core company details and proof of qualifications the Digital Brain uses when introducing your business."
      />

      <SubHeading title="1.1 Company Names" />
      <FormInput
        label="Official Legal Name"
        sublabel="Used for legal / formal correspondence."
        required
        type="text"
        placeholder="e.g., Acme Services Pty Ltd"
        value={kb.company_name}
        onChange={(e) => updateKB("company_name", e.target.value)}
        error={errors.company_name}
        icon={Building2}
        data-testid="renovation-company-name-input"
      />
      <FormInput
        label="Trading Name"
        sublabel="What the Digital Brain uses when presenting to customers."
        type="text"
        placeholder="e.g., Acme Services"
        value={kb.trading_name || ""}
        onChange={(e) => updateKB("trading_name", e.target.value)}
        icon={Briefcase}
      />
      <FormInput
        label="Company Phone"
        type="tel"
        placeholder="+61 2 4222 0001"
        value={kb.company_phone || ""}
        onChange={(e) => updateKB("company_phone", e.target.value)}
        icon={Phone}
      />
      <FormInput
        label="Company Email"
        type="email"
        placeholder="hello@company.com.au"
        value={kb.company_email || ""}
        onChange={(e) => updateKB("company_email", e.target.value)}
        icon={Mail}
      />
      <FormInput
        label="Tagline"
        type="text"
        placeholder="Renovations you can plan with confidence"
        value={formData.tone_of_voice?.tagline || ""}
        onChange={(e) =>
          setFormData({
            ...formData,
            tone_of_voice: { ...formData.tone_of_voice, tagline: e.target.value },
          })
        }
        icon={Sparkles}
      />
      <StringListEditor
        label="Founders"
        values={kb.founders || []}
        onChange={(next) => updateKB("founders", next)}
        placeholder="Founder name"
        icon={User}
      />

      <SubHeading title="1.2 Registration Number" />
      <FormInput
        label="ABN / EIN / Registration Number"
        sublabel="Used when responding to B2B client inquiries."
        type="text"
        placeholder="e.g., 12 345 678 901"
        value={kb.credentials?.registration_number || ""}
        onChange={(e) => updateCred("registration_number", e.target.value)}
        icon={FileText}
      />

      <SubHeading title="1.3 Licensing" />
      <FormInput
        label="Contractor License Number"
        type="text"
        placeholder="#ROC12345"
        value={kb.credentials?.license_number || ""}
        onChange={(e) => updateCred("license_number", e.target.value)}
        icon={ShieldCheck}
      />
      <FormInput
        label="License Holder Name"
        type="text"
        placeholder="e.g., Marcus Thorp"
        value={kb.credentials?.license_holder_name || ""}
        onChange={(e) => updateCred("license_holder_name", e.target.value)}
        icon={User}
      />
      <FormTextarea
        label="Insurance Coverage"
        rows={2}
        value={kb.credentials?.insurance || ""}
        onChange={(e) => updateCred("insurance", e.target.value)}
        icon={ShieldCheck}
      />
      <FormTextarea
        label="Warranty Terms"
        rows={2}
        value={kb.credentials?.warranty_terms || ""}
        onChange={(e) => updateCred("warranty_terms", e.target.value)}
        icon={ShieldCheck}
      />

      <SubHeading title="1.4 Year Founded" />
      <FormInput
        label="Year Founded"
        sublabel="Used by the Digital Brain to build trust and handle price objections."
        type="number"
        min={1800}
        max={new Date().getFullYear()}
        placeholder="e.g., 2008"
        value={kb.founded_year || ""}
        onChange={(e) =>
          updateKB("founded_year", e.target.value ? parseInt(e.target.value, 10) : null)
        }
        icon={Calendar}
      />

      <SubHeading title="1.5 Website URL" />
      <FormInput
        label="Website URL"
        sublabel="Link customers can visit to review your portfolio."
        type="url"
        placeholder="https://premier-renovations.com.au"
        value={kb.website_url || ""}
        onChange={(e) => updateKB("website_url", e.target.value)}
        error={errors.website_url}
        icon={Globe}
      />

      <SubHeading title="1.6 Social Media" />
      <p className="text-[#9CA3AF] text-xs font-body -mt-1">
        Links (Instagram, Facebook, etc.) the Digital Brain can share to redirect leads.
      </p>
      <div className="space-y-3">
        {socials.map((s, idx) => (
          <div key={idx} className="p-3 rounded-lg bg-white/5 border border-white/10 space-y-2">
            <div className="flex items-center justify-between">
              <span className="font-mono text-[#00D1FF] text-xs">Social #{idx + 1}</span>
              <button
                type="button"
                onClick={() => setSocials(socials.filter((_, i) => i !== idx))}
                className="text-[#9CA3AF] hover:text-red-400 transition-colors p-1"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              <input
                type="text"
                value={s.network || ""}
                onChange={(e) =>
                  setSocials(socials.map((it, i) => (i === idx ? { ...it, network: e.target.value } : it)))
                }
                placeholder="Network (e.g., instagram)"
                className="md:col-span-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-[#F3F4F6] placeholder-[#9CA3AF]/50 focus:outline-none focus:border-[#00D1FF]/50 text-sm font-body"
              />
              <input
                type="url"
                value={s.url || ""}
                onChange={(e) =>
                  setSocials(socials.map((it, i) => (i === idx ? { ...it, url: e.target.value } : it)))
                }
                placeholder="https://instagram.com/yourcompany"
                className="md:col-span-2 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-[#F3F4F6] placeholder-[#9CA3AF]/50 focus:outline-none focus:border-[#00D1FF]/50 text-sm font-body"
              />
            </div>
          </div>
        ))}
      </div>
      <button
        type="button"
        onClick={() => setSocials([...(socials || []), { network: "", url: "" }])}
        className="flex items-center gap-2 text-[#00D1FF] text-sm font-body hover:text-[#00D1FF]/80 transition-colors py-2"
      >
        <Plus className="w-4 h-4" />
        Add Social Network
      </button>

      <SubHeading title="1.7 Office & Physical Address" />
      <FormTextarea
        label="Office Address"
        sublabel="Primary business address. Also used as the depot reference for drive-radius checks."
        rows={2}
        placeholder="123 Main Street, Wollongong NSW 2500"
        value={kb.office_location?.address || ""}
        onChange={(e) => updateOffice("address", e.target.value)}
        icon={MapPin}
      />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FormInput
          label="Country"
          sublabel="Country where your office is located."
          placeholder="e.g., Australia"
          value={kb.office_location?.country || ""}
          onChange={(e) => updateOffice("country", e.target.value)}
          icon={Globe}
        />
        <FormInput
          label="State / Region"
          sublabel="State, province or region of your office."
          placeholder="e.g., NSW"
          value={kb.office_location?.state || ""}
          onChange={(e) => updateOffice("state", e.target.value)}
          icon={MapPin}
        />
      </div>
      <ToggleSwitch
        label="Is this a customer-facing address?"
        description="If enabled, the Digital Brain may share this address with customers who ask."
        checked={!!kb.office_location?.is_customer_facing}
        onChange={(v) => updateOffice("is_customer_facing", v)}
        icon={Home}
      />
    </div>
  );
};

// ------------------------------------------------------------
// Step 2: Service Area
// ------------------------------------------------------------
const Step2ServiceArea = ({ formData, setFormData }) => {
  const sa = formData.knowledge_base.service_area || {};
  const update = (field, value) =>
    setFormData({
      ...formData,
      knowledge_base: {
        ...formData.knowledge_base,
        service_area: { ...sa, [field]: value },
      },
    });
  const updateRegional = (field, value) =>
    update("regional_callouts", { ...(sa.regional_callouts || {}), [field]: value });

  const zipInput = (sa.zip_codes || []).join(", ");

  return (
    <div className="space-y-6" data-testid="renovation-step-2">
      <SectionHeading
        title="Service Area"
        description="Where you operate and how far you're willing to travel."
      />

      <SubHeading title="2.1 Coverage Area" />
      <FormInput
        label="Zip Codes / Postcodes"
        sublabel="Comma-separated list (use 'any' to cover everything)."
        type="text"
        placeholder="e.g., 2500, 2505, 2508 or 'any'"
        value={zipInput}
        onChange={(e) =>
          update(
            "zip_codes",
            e.target.value
              .split(",")
              .map((s) => s.trim())
              .filter(Boolean)
          )
        }
        icon={MapPin}
      />
      <FormTextarea
        label="Suburbs or LGAs"
        sublabel="List of suburbs, council areas or regions you serve."
        rows={2}
        placeholder="e.g., Wollongong, Shellharbour, Sutherland Shire"
        value={sa.suburbs_or_lgas || ""}
        onChange={(e) => update("suburbs_or_lgas", e.target.value)}
        icon={MapPin}
      />
      <FormTextarea
        label="Description"
        sublabel="Human-friendly summary of the coverage area."
        rows={2}
        placeholder="Illawarra and South Sydney regions"
        value={sa.description || ""}
        onChange={(e) => update("description", e.target.value)}
        icon={Info}
      />

      <SubHeading title="2.2 Maximum Radius" />
      <FormInput
        label="Maximum Drive Radius (km)"
        sublabel="Beyond this distance the Digital Brain auto-declines."
        type="number"
        min={0}
        placeholder="100"
        value={sa.max_drive_radius_km || ""}
        onChange={(e) =>
          update("max_drive_radius_km", e.target.value ? parseInt(e.target.value, 10) : 0)
        }
        icon={Target}
      />

      <SubHeading title="2.3 State Boundaries" />
      <ToggleSwitch
        label="Cross state / region borders"
        description="Enable if jobs across neighboring regions are allowed."
        checked={!!sa.crosses_state_borders}
        onChange={(v) => update("crosses_state_borders", v)}
        icon={Scale}
      />

      <SubHeading title="2.4 Regional Callouts" />
      <ToggleSwitch
        label="Allow regional callouts"
        description="Do you travel far outside the city for larger jobs?"
        checked={!!sa.regional_callouts?.allowed}
        onChange={(v) => updateRegional("allowed", v)}
        icon={MapPin}
      />
      {sa.regional_callouts?.allowed && (
        <FormInput
          label="Minimum Job Value for Regional Callouts"
          sublabel="Only travel if the job is worth at least this amount."
          type="text"
          placeholder="e.g., $10,000"
          value={sa.regional_callouts?.min_job_value || ""}
          onChange={(e) => updateRegional("min_job_value", e.target.value)}
          icon={DollarSign}
        />
      )}

      <SubHeading title="2.5 Out-of-Area Message" />
      <FormTextarea
        label="Rejection Message"
        sublabel="Shown when the property is outside your service area (Agent 3)."
        rows={3}
        value={sa.rejection_message || ""}
        onChange={(e) => update("rejection_message", e.target.value)}
        icon={MapPin}
      />
    </div>
  );
};

// ------------------------------------------------------------
// Step 3: Services Offered
// ------------------------------------------------------------
const SERVICE_GROUPS = [
  {
    title: "3.1 Services",
    items: [
      { key: "bathroom_renovation", label: "Bathroom renovation" },
      { key: "kitchen_renovation", label: "Kitchen renovation" },
      { key: "laundry_renovation", label: "Laundry renovation" },
      { key: "whole_home_renovation", label: "Whole-home renovation" },
      { key: "partial_home_renovation", label: "Partial / multi-room renovation" },
      { key: "extension_addition", label: "Home extension / addition" },
      { key: "granny_flat_secondary_dwelling", label: "Granny flat / ADU" },
    ],
  },
  {
    title: "3.2 Trade Packages",
    items: [
      { key: "flooring_installation", label: "Flooring (timber, hybrid, tile)" },
      { key: "wall_tiling", label: "Wall & floor tiling" },
      { key: "painting_decorating", label: "Painting & decorating" },
    ],
  },
  {
    title: "3.3 Specialty & Optional",
    items: [
      { key: "insurance_restoration_repair", label: "Insurance restoration (water/fire)" },
      { key: "commercial_fit_out", label: "Commercial fit-out / shopfront" },
      { key: "design_consult_only", label: "Design consultation only (no build)" },
      { key: "structural_engineering_only", label: "Structural engineering only" },
      { key: "new_build_construction_only", label: "New build construction only" },
      { key: "asbestos_removal_standalone", label: "Asbestos removal (standalone)" },
      { key: "pool_spa_installation", label: "Pool / spa installation" },
      { key: "landscaping_only", label: "Landscaping only" },
      { key: "architectural_drawings_only", label: "Architectural drawings only" },
    ],
  },
];

const Step3Services = ({ formData, setFormData }) => {
  const services = formData.knowledge_base.services_offered || {};
  const updateService = (key, value) =>
    setFormData({
      ...formData,
      knowledge_base: {
        ...formData.knowledge_base,
        services_offered: { ...services, [key]: value },
      },
    });
  const updateOther = (field, value) =>
    updateService("other_services", { ...(services.other_services || {}), [field]: value });

  const oos = formData.knowledge_base?.out_of_scope_services || [];
  const setOOS = (next) =>
    setFormData({
      ...formData,
      knowledge_base: {
        ...formData.knowledge_base,
        out_of_scope_services: next,
      },
    });

  return (
    <div className="space-y-6" data-testid="renovation-step-3">
      <SectionHeading
        title="Services Offered"
        description="Your competency matrix — what you do, what you don't, and when."
      />

      {SERVICE_GROUPS.map((group) => (
        <div key={group.title}>
          <SubHeading title={group.title} />
          <div className="space-y-2">
            {group.items.map((it) => (
              <TriStatePicker
                key={it.key}
                label={it.label}
                value={services[it.key]}
                onChange={(v) => updateService(it.key, v)}
              />
            ))}
          </div>
        </div>
      ))}

      <SubHeading title="3.4 Out-of-Scope Services" />
      <StringListEditor
        label="Services you never provide"
        sublabel="Used by the Digital Brain for instant disqualification."
        values={oos}
        onChange={setOOS}
        placeholder="e.g., structural_engineering_only"
        icon={AlertTriangle}
      />
    </div>
  );
};

// Small inline tri-state (Yes / Sometimes / No)
const TriStateInline = ({ value, onChange }) => {
  const opts = [
    { v: "true", label: "Yes" },
    { v: "sometimes", label: "Sometimes" },
    { v: "false", label: "No" },
  ];
  return (
    <div className="flex gap-1.5">
      {opts.map((o) => {
        const active = (value || "false") === o.v;
        return (
          <button
            key={o.v}
            type="button"
            onClick={() => onChange(o.v)}
            className={`px-3 py-1.5 rounded-md text-xs font-body border transition-all ${
              active
                ? "bg-[#00D1FF]/15 border-[#00D1FF]/50 text-[#00D1FF]"
                : "bg-white/5 border-white/10 text-[#9CA3AF] hover:border-white/20"
            }`}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
};

// ------------------------------------------------------------
// Step 4: Appointment Types & Booking Rules
// ------------------------------------------------------------

const Step4Appointments = ({ formData, setFormData, ghlIntegrationActive = false }) => {
  const sch = formData.scheduling || {};
  const apps = sch.appointment_types || {};
  const rules = sch.booking_rules || {};
  const mediaReq = sch.media_requirements || {};

  const updateSch = (field, value) =>
    setFormData({ ...formData, scheduling: { ...sch, [field]: value } });

  const updateApp = (key, field, value) =>
    setFormData({
      ...formData,
      scheduling: {
        ...sch,
        appointment_types: {
          ...apps,
          [key]: { ...(apps[key] || {}), [field]: value },
        },
      },
    });

  const updateRules = (field, value) =>
    setFormData({
      ...formData,
      scheduling: {
        ...sch,
        booking_rules: { ...rules, [field]: value },
      },
    });

  return (
    <div className="space-y-6" data-testid="renovation-step-4">
      <SectionHeading
        title="Appointment Types & Booking Rules"
        description="Configure which visit types the Digital Brain can offer and the rules around scheduling them."
      />

      <SubHeading title="4.1 Scheduling Defaults" />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FormInput
          label="Meeting Type"
          type="text"
          placeholder="onsite_consultation"
          value={sch.meeting_type || ""}
          onChange={(e) => updateSch("meeting_type", e.target.value)}
          icon={Calendar}
        />
        <FormInput
          label="Default Duration (minutes)"
          type="number"
          min={0}
          value={sch.default_duration_minutes ?? ""}
          onChange={(e) =>
            updateSch(
              "default_duration_minutes",
              e.target.value ? parseInt(e.target.value, 10) : 0
            )
          }
          icon={Clock}
        />
      </div>

      {ghlIntegrationActive && (
        <>
          <SubHeading title="4.2 Team Calendars (GHL)" />
          <p className="text-[#9CA3AF] text-xs font-body -mt-1">
            Map staff calendars to appointment types. Connect calendar IDs via Integrations → GoHighLevel.
          </p>
          <TeamCalendarsEditor
            calendars={sch.team_calendars || []}
            onChange={(next) => updateSch("team_calendars", next)}
            appointmentTypeKeys={CANONICAL_APPOINTMENT_TYPE_KEYS}
          />
        </>
      )}

      <SubHeading title="4.3 Default Photo Prompts" />
      <StringListEditor
        label="Media requirements — photo prompts"
        values={mediaReq.default_photo_prompts || []}
        onChange={(next) =>
          updateSch("media_requirements", { ...mediaReq, default_photo_prompts: next })
        }
        placeholder="Wide shot of the affected area"
        icon={FileText}
      />

      <SubHeading title="4.4 Visit & Call Appointments" />
      <div className="space-y-4">
        {CANONICAL_APPOINTMENT_TYPE_KEYS.map((key) => {
          const a = apps[key] || {};
          const label = APPOINTMENT_TYPE_LABELS[key] || key;
          const isCallType = key === "phone_video_consult";
          const showFields = isCallType || a.is_offered;
          return (
            <div
              key={key}
              className="p-4 rounded-xl bg-white/5 border border-white/10 space-y-3"
            >
              <div className="flex items-center justify-between gap-3">
                <div>
                  <h4 className="text-[#F3F4F6] text-sm font-heading font-bold">{label}</h4>
                  <p className="text-[#9CA3AF] text-xs font-body">{key}</p>
                </div>
                {!isCallType && (
                  <button
                    type="button"
                    onClick={() => updateApp(key, "is_offered", !a.is_offered)}
                    className={`relative w-11 h-6 rounded-full transition-colors ${
                      a.is_offered ? "bg-[#00D1FF]" : "bg-white/20"
                    }`}
                    aria-pressed={!!a.is_offered}
                  >
                    <div
                      className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
                        a.is_offered ? "translate-x-6" : "translate-x-1"
                      }`}
                    />
                  </button>
                )}
              </div>

              {showFields && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <FormInput
                    label="Duration (minutes)"
                    type="number"
                    min={0}
                    value={a.duration_minutes || ""}
                    onChange={(e) =>
                      updateApp(
                        key,
                        "duration_minutes",
                        e.target.value ? parseInt(e.target.value, 10) : 0
                      )
                    }
                    icon={Clock}
                  />
                  <FormInput
                    label="Attendee / Assigned Staff"
                    type="text"
                    placeholder="e.g., Sales Representative"
                    value={a.attendee || ""}
                    onChange={(e) => updateApp(key, "attendee", e.target.value)}
                    icon={User}
                  />
                  <div className="md:col-span-2">
                    <ToggleSwitch
                      label="Is this appointment free?"
                      checked={!!a.is_free}
                      onChange={(v) => updateApp(key, "is_free", v)}
                      icon={DollarSign}
                    />
                  </div>
                  {!a.is_free && (
                    <div className="md:col-span-2">
                      <FormInput
                        label="Price / Fee"
                        type="text"
                        placeholder="e.g., $150 report fee"
                        value={a.price || ""}
                        onChange={(e) => updateApp(key, "price", e.target.value)}
                        icon={DollarSign}
                      />
                    </div>
                  )}
                  <div className="md:col-span-2">
                    <StringListEditor
                      label="Prerequisites"
                      sublabel="Required info before booking (address, photos, etc.)"
                      values={a.prerequisites || []}
                      onChange={(next) => updateApp(key, "prerequisites", next)}
                      placeholder="e.g., address"
                      icon={FileText}
                    />
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <SubHeading title="4.5 Min / Max Lead Time" />
      <div className="grid grid-cols-1 gap-4">
        <FormInput
          label="Meeting — Minimum Lead Time (hours)"
          sublabel="No on-site inspections sooner than X hours from now."
          type="number"
          min={0}
          value={rules.visit_min_lead_time_hours ?? ""}
          onChange={(e) =>
            updateRules(
              "visit_min_lead_time_hours",
              e.target.value ? parseInt(e.target.value, 10) : 0
            )
          }
          icon={Clock}
        />
        <FormInput
          label="Call — Minimum Lead Time (hours)"
          sublabel="No phone/video consult bookings sooner than X hours from now."
          type="number"
          min={0}
          value={rules.call_min_lead_time_hours ?? ""}
          onChange={(e) =>
            updateRules(
              "call_min_lead_time_hours",
              e.target.value ? parseInt(e.target.value, 10) : 0
            )
          }
          icon={Clock}
        />
        <FormInput
          label="Maximum Lead Time (days)"
          sublabel="No bookings further than Y days in the future."
          type="number"
          min={0}
          value={rules.max_lead_time_days || ""}
          onChange={(e) =>
            updateRules(
              "max_lead_time_days",
              e.target.value ? parseInt(e.target.value, 10) : 0
            )
          }
          icon={Calendar}
        />
      </div>

      <SubHeading title="4.6 Skip Weekday Conditions" />
      <SkipWeekdayConditionsEditor
        value={rules.skip_weekday_conditions}
        onChange={(next) => updateRules("skip_weekday_conditions", next)}
        ghlIntegrationActive={ghlIntegrationActive}
      />

    </div>
  );
};

// ------------------------------------------------------------
// Step 5: Booking Calendar — Hours & Availability
// ------------------------------------------------------------
const Step5Calendar = ({ formData, setFormData }) => {
  const sch = formData.scheduling || {};
  const oh = sch.operating_hours || {};
  const logistics = sch.logistics || {};
  const channelSchedules = sch.channel_schedules || {};
  const siteVisit = channelSchedules.site_visit || {};
  const phoneConsult = channelSchedules.phone_consult || {};

  const updateOH = (field, value) =>
    setFormData({
      ...formData,
      scheduling: {
        ...sch,
        operating_hours: { ...oh, [field]: value },
      },
    });
  const updateChannelSchedule = (channel, field, value) =>
    setFormData({
      ...formData,
      scheduling: {
        ...sch,
        channel_schedules: {
          ...channelSchedules,
          [channel]: { ...(channelSchedules[channel] || {}), [field]: value },
        },
      },
    });
  const updateSiteVisit = (field, value) => updateChannelSchedule("site_visit", field, value);
  const updatePhoneConsult = (field, value) => updateChannelSchedule("phone_consult", field, value);
  const updateLogistics = (field, value) =>
    setFormData({
      ...formData,
      scheduling: {
        ...sch,
        logistics: { ...logistics, [field]: value },
      },
    });

  return (
    <div className="space-y-6" data-testid="renovation-step-5">
      <SectionHeading
        title="Booking Calendar — Hours & Availability"
        description="Define when customers can book appointments and the rules around them. Unavailable times are controlled by your connected calendar (GHL)."
      />

      <SubHeading title="5.1 Site Visit Hours" />
      <p className="text-[#9CA3AF] text-xs font-body">
        When consultants can visit the property for measure & quote appointments.
      </p>
      <div className="space-y-2">
        {WEEKDAY_ROWS.map(({ key, label }) => (
          <DayWindowRow
            key={key}
            label={label}
            dayValue={siteVisit[key]}
            onChange={(v) => updateSiteVisit(key, v)}
          />
        ))}
      </div>

      <SubHeading title="5.2 Phone Consult Hours" />
      <p className="text-[#9CA3AF] text-xs font-body">
        When the office can schedule phone or video callbacks with clients.
      </p>
      <div className="space-y-2">
        {WEEKDAY_ROWS.map(({ key, label }) => (
          <DayWindowRow
            key={key}
            label={label}
            dayValue={phoneConsult[key]}
            onChange={(v) => updatePhoneConsult(key, v)}
          />
        ))}
      </div>
      <FormTextarea
        label="Out-of-Hours Message"
        sublabel="Shown when customers contact the office outside phone consult hours."
        rows={2}
        placeholder="Our office is currently closed..."
        value={phoneConsult.out_of_hours_message || ""}
        onChange={(e) => updatePhoneConsult("out_of_hours_message", e.target.value)}
        icon={MessageSquare}
      />

      <SubHeading title="5.3 Public Holidays" />
      <p className="text-[#9CA3AF] text-xs font-body">
        Dates when the office is closed or surcharged. Agent 7 expects an array with date, name, and fee.
      </p>
      <PublicHolidaysEditor
        holidays={Array.isArray(oh.public_holidays) ? oh.public_holidays : []}
        onChange={(next) => updateOH("public_holidays", next)}
      />

      <SubHeading title="5.4 Travel Buffer" />
      <FormInput
        label="Travel Buffer (minutes)"
        sublabel="Time automatically added between back-to-back appointments."
        type="number"
        min={0}
        value={logistics.travel_buffer_minutes || ""}
        onChange={(e) =>
          updateLogistics(
            "travel_buffer_minutes",
            e.target.value ? parseInt(e.target.value, 10) : 0
          )
        }
        icon={Clock}
      />

      <SubHeading title="5.5 Time Format" />
      <RadioGroup
        label="How should the Digital Brain quote times?"
        options={[
          {
            value: "exact_time",
            label: "Exact time",
            description: "e.g., 10:00 AM sharp",
          },
          {
            value: "arrival_window",
            label: "Arrival window",
            description: "e.g., Between 10:00 and 12:00",
          },
        ]}
        value={logistics.appointment_time_format || "arrival_window"}
        onChange={(v) => updateLogistics("appointment_time_format", v)}
        icon={Clock}
      />
      {logistics.appointment_time_format === "arrival_window" && (
        <FormInput
          label="Window Size (hours)"
          sublabel="e.g., 2 for a 2-hour arrival window."
          type="number"
          min={1}
          value={logistics.window_size_hours || ""}
          onChange={(e) =>
            updateLogistics(
              "window_size_hours",
              e.target.value ? parseInt(e.target.value, 10) : 0
            )
          }
          icon={Clock}
        />
      )}

      <SubHeading title="5.6 Daily Limit" />
      <FormInput
        label="Max Appointments per Day"
        sublabel="Prevents the calendar from getting overloaded."
        type="number"
        min={0}
        value={logistics.max_appointments_per_day || ""}
        onChange={(e) =>
          updateLogistics(
            "max_appointments_per_day",
            e.target.value ? parseInt(e.target.value, 10) : 0
          )
        }
        icon={Target}
      />

      <SubHeading title="5.7 Custom Rules" />
      <StringListEditor
        label="Custom Schedule Rules"
        sublabel="Special conditions (e.g., 'No estimates on Fridays')."
        values={oh.custom_schedule_rules || []}
        onChange={(next) => updateOH("custom_schedule_rules", next)}
        placeholder="e.g., No estimates on Fridays"
        icon={FileText}
      />
    </div>
  );
};

// ------------------------------------------------------------
// Step 6: Lead Qualification — Questions & Logic
// ------------------------------------------------------------
const Step7Qualification = ({ formData, setFormData }) => {
  const ql = formData.qualification_logic || {};
  const questions = Array.isArray(ql.questions) ? ql.questions : [];

  const update = (field, value) =>
    setFormData({
      ...formData,
      qualification_logic: { ...ql, [field]: value },
    });
  const ip = ql.interviewer_policy || {};
  const updatePolicy = (field, value) =>
    setFormData({
      ...formData,
      qualification_logic: {
        ...ql,
        interviewer_policy: { ...ip, [field]: value },
      },
    });

  return (
    <div className="space-y-6" data-testid="renovation-step-6">
      <SectionHeading
        title="Lead Qualification — Questions & Logic"
        description="Configure your Digital Brain during lead qualification."
      />

      <HiddenUi hidden={RENOVATION_UI_HIDDEN.knownUserHandling_6_1}>
        <SubHeading title="6.1 Known User Handling" />
        <ToggleSwitch
          label="Skip qualification for known users"
          description="If the Digital Brain recognizes an existing customer, skip repeat questions."
          checked={!!ql.skip_qualification_if_known_user}
          onChange={(v) => update("skip_qualification_if_known_user", v)}
          icon={User}
        />
        <ToggleSwitch
          label="Strict qualification gate"
          description="Block scheduling until required qualification is answered."
          checked={!!ql.strict_qualification_gate}
          onChange={(v) => update("strict_qualification_gate", v)}
          icon={ShieldCheck}
        />
        <ToggleSwitch
          label="Lite qualification (5-field booking gate)"
          description="Allow free consultation booking after name, phone, email, suburb, and scope."
          checked={!!ql.lite_qualification_enabled}
          onChange={(v) => update("lite_qualification_enabled", v)}
          icon={Target}
        />
        <SubHeading title="6.2 Booking Gate Keys" />
        <p className="text-[#9CA3AF] text-xs font-body">
          Derived from qualification questions — required before lite booking is allowed.
        </p>
        <BookingGateKeysDisplay keys={ql.booking_gate_keys || []} />
      </HiddenUi>

      <FormInput
        label="Unanswered question retry limit"
        sublabel="1 = ask each question once. 3 = retry up to three times if the lead does not answer."
        type="number"
        min={1}
        max={5}
        step={1}
        value={ql.interviewer_policy?.repetition_soft_ask_threshold ?? 3}
        onChange={(e) => {
          const n = Math.min(5, Math.max(1, Number(e.target.value) || 3));
          updatePolicy("repetition_soft_ask_threshold", n);
        }}
      />

      <SubHeading title="6.3 Questionnaire" />
      <p className="text-[#9CA3AF] text-xs font-body">
        Order is the ask order for new conversations. Open chats keep their current sequence.
      </p>
      <QuestionnaireEditor
        questions={questions}
        onChange={(next) => update("questions", next)}
      />
    </div>
  );
};

// ------------------------------------------------------------
// Step 7: Disqualifiers — Auto-Decline Rules
// ------------------------------------------------------------
const PROPERTY_TYPE_OPTIONS = [
  { v: "commercial", label: "Commercial" },
  { v: "strata", label: "Strata / Body Corporate" },
  { v: "townhouse", label: "Townhouse" },
  { v: "unit", label: "Unit / Apartment" },
  { v: "industrial", label: "Industrial" },
  { v: "heritage", label: "Heritage Listed" },
];

const TENANT_POLICY_OPTIONS = [
  { value: "", label: "(none)" },
  ...CANONICAL_DISQUAL_ENUMS.tenant_policy.map((v) => ({
    value: v,
    label:
      v === "require_owner_consent"
        ? "Require owner consent first"
        : v === "accept"
          ? "Accept tenant requests"
          : "Decline tenant requests",
  })),
];

const ASBESTOS_POLICY_OPTIONS = [
  { value: "", label: "(none)" },
  ...CANONICAL_DISQUAL_ENUMS.asbestos_policy.map((v) => ({
    value: v,
    label:
      v === "refer_to_licensed_asbestos_specialist"
        ? "Refer to licensed asbestos specialist"
        : v === "refer_to_partner"
          ? "Refer to a licensed partner"
          : v === "accept"
            ? "We handle asbestos removal"
            : "Politely decline",
  })),
];

const UNQUALIFIED_ACTION_OPTIONS = CANONICAL_DISQUAL_ENUMS.unqualified_action.map((v) => ({
  value: v,
  label:
    v === "decline_politely"
      ? "Politely decline"
      : v === "refer_to_specialist"
        ? "Refer to specialist"
        : v === "manager"
          ? "Escalate to manager"
          : "Redirect to website",
}));

const Step8Disqualifiers = ({ formData, setFormData }) => {
  const ql = formData.qualification_logic || {};
  const dr = ql.disqualification_rules || {};

  const updateDR = (field, value) =>
    setFormData({
      ...formData,
      qualification_logic: {
        ...ql,
        disqualification_rules: { ...dr, [field]: value },
      },
    });
  const updateOptionalPolicy = (field, value) => {
    const next = { ...dr };
    if (!value) delete next[field];
    else next[field] = value;
    setFormData({
      ...formData,
      qualification_logic: { ...ql, disqualification_rules: next },
    });
  };
  const updateRej = (field, value) =>
    updateDR("rejection_messages", { ...(dr.rejection_messages || {}), [field]: value });

  const toggleBlocked = (val) => {
    const list = dr.blocked_property_types || [];
    const next = list.includes(val) ? list.filter((x) => x !== val) : [...list, val];
    updateDR("blocked_property_types", next);
  };

  return (
    <div className="space-y-6" data-testid="renovation-step-7">
      <SectionHeading
        title="Disqualifiers — Auto-Decline Rules"
        description="Rules for politely declining leads that don't fit your business."
      />

      <SubHeading title="7.1 Disqualification Limits" />

      <div className="space-y-2">
        <label className="flex items-center gap-2 text-[#F3F4F6] text-sm font-body">
          <AlertTriangle className="w-4 h-4 text-[#00D1FF]" />
          Blocked types
        </label>
        <p className="text-[#9CA3AF] text-xs font-body">Select the types you do NOT accept.</p>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {PROPERTY_TYPE_OPTIONS.map((pt) => {
            const active = (dr.blocked_property_types || []).includes(pt.v);
            return (
              <button
                key={pt.v}
                type="button"
                onClick={() => toggleBlocked(pt.v)}
                className={`px-3 py-2 rounded-lg text-sm border transition-all font-body ${
                  active
                    ? "bg-red-500/15 border-red-400/50 text-red-300"
                    : "bg-white/5 border-white/10 text-[#9CA3AF] hover:border-white/20"
                }`}
              >
                {pt.label}
              </button>
            );
          })}
        </div>
      </div>

      <details className="rounded-lg border border-white/10 bg-white/[0.03] p-3">
        <summary className="cursor-pointer text-sm text-[#9CA3AF] font-body select-none">
          Legacy / optional housing policies (usually leave as none)
        </summary>
        <div className="mt-3 space-y-4">
          <RadioGroup
            label="Optional authorization policy"
            options={TENANT_POLICY_OPTIONS}
            value={dr.tenant_policy || ""}
            onChange={(v) => updateOptionalPolicy("tenant_policy", v)}
            icon={Users}
          />

          <RadioGroup
            label="Asbestos Policy"
            options={ASBESTOS_POLICY_OPTIONS}
            value={dr.asbestos_policy || ""}
            onChange={(v) => updateOptionalPolicy("asbestos_policy", v)}
            icon={AlertTriangle}
          />
        </div>
      </details>

      <FormInput
        label="Minimum project budget"
        sublabel="Leave empty to disable. Enter the amount in your local currency (e.g. 5000). Leads who state a lower budget will be politely declined. Exact match meets the minimum. If the budget question is optional and skipped, this rule does not apply."
        type="number"
        min={1}
        placeholder="e.g., 5000"
        value={dr.min_budget ?? ""}
        onChange={(e) => {
          const raw = e.target.value;
          if (raw === "" || raw == null) {
            const next = { ...dr };
            delete next.min_budget;
            setFormData({
              ...formData,
              qualification_logic: { ...ql, disqualification_rules: next },
            });
            return;
          }
          const n = parseInt(raw, 10);
          updateDR("min_budget", Number.isFinite(n) && n > 0 ? n : undefined);
        }}
        icon={AlertTriangle}
      />

      <SubHeading title="7.2 Custom Restrictions" />
      <StringListEditor
        label="Custom Auto-Declines"
        sublabel="e.g., We don't take standalone engineering-only jobs"
        values={dr.custom_auto_declines || []}
        onChange={(next) => updateDR("custom_auto_declines", next)}
        placeholder="e.g., New build only (no renovation)"
        icon={AlertTriangle}
      />

      <SubHeading title="7.3 Action on Disqualification" />
      <RadioGroup
        label="What should the Digital Brain do with a disqualified lead?"
        options={UNQUALIFIED_ACTION_OPTIONS}
        value={ql.unqualified_action || "decline_politely"}
        onChange={(v) =>
          setFormData({
            ...formData,
            qualification_logic: { ...ql, unqualified_action: v },
          })
        }
        icon={MessageSquare}
      />

      <SubHeading title="7.4 Rejection Scripts" />
      <p className="text-[#9CA3AF] text-xs font-body">
        Predefined polite responses for each rejection reason.
      </p>

      {[
        { key: "out_of_area", label: "Out of service area" },
        { key: "service_not_offered", label: "Service not offered" },
        { key: "property_type", label: "Blocked type" },
        { key: "budget_too_low", label: "Budget below minimum" },
        { key: "default_polite_no", label: "Default polite no" },
      ].map((item) => (
        <FormTextarea
          key={item.key}
          label={item.label}
          rows={2}
          value={dr.rejection_messages?.[item.key] || ""}
          onChange={(e) => updateRej(item.key, e.target.value)}
          placeholder={`Message shown when: ${item.label}`}
          icon={MessageSquare}
        />
      ))}
    </div>
  );
};

// ------------------------------------------------------------
// Step 8: Pricing & Quote Policy
// ------------------------------------------------------------
const Step9Pricing = ({ formData, setFormData }) => {
  const pp = formData.knowledge_base.pricing_policy || {};

  const updatePP = (field, value) =>
    setFormData({
      ...formData,
      knowledge_base: {
        ...formData.knowledge_base,
        pricing_policy: { ...pp, [field]: value },
      },
    });
  const updateQR = (field, value) =>
    updatePP("quote_rules", { ...(pp.quote_rules || {}), [field]: value });
  const updateJR = (field, value) =>
    updatePP("job_requirements", { ...(pp.job_requirements || {}), [field]: value });
  const updatePF = (field, value) =>
    updatePP("payment_and_finance", { ...(pp.payment_and_finance || {}), [field]: value });
  const updateFin = (field, value) =>
    updatePF("financing_options", { ...(pp.payment_and_finance?.financing_options || {}), [field]: value });
  const updateBP = (field, value) =>
    updatePP("ballpark_pricing", { ...(pp.ballpark_pricing || {}), [field]: value });

  const products = pp.ballpark_pricing?.products || [];
  const setProducts = (next) => updateBP("products", next);

  const paymentMethods = pp.payment_and_finance?.payment_methods || [];

  return (
    <div className="space-y-6" data-testid="renovation-step-8">
      <SectionHeading
        title="Pricing & Quote Policy"
        description="How the Digital Brain discusses pricing, fees and payment with customers."
      />

      <SubHeading title="8.1 Free Estimate Standard" />
      <ToggleSwitch
        label="Free estimates are standard"
        description="Offered across all services by default."
        checked={!!pp.quote_rules?.free_quotes_standard}
        onChange={(v) => updateQR("free_quotes_standard", v)}
        icon={DollarSign}
      />
      <ToggleSwitch
        label="Fixed-Price Guarantee enabled"
        description="Allows the Digital Brain to mention Fixed-Price Guarantee as a brand USP (not instant job totals)."
        checked={!!pp.quote_rules?.fixed_price_guarantee_enabled}
        onChange={(v) => updateQR("fixed_price_guarantee_enabled", v)}
        icon={ShieldCheck}
      />
      {pp.quote_rules?.fixed_price_guarantee_enabled ? (
        <FormTextarea
          label="Fixed-Price lock wording"
          sublabel="Verbatim sentence the Digital Brain may use when explaining when the contract price is locked (from your KB, not hardcoded in prompts)."
          rows={2}
          placeholder="Your contract price is locked in after the design / quote stage"
          value={pp.quote_rules?.fixed_price_guarantee_lock_description || ""}
          onChange={(e) =>
            updateQR("fixed_price_guarantee_lock_description", e.target.value)
          }
          icon={ShieldCheck}
        />
      ) : null}

      <SubHeading title="8.2 Job Requirements" />
      <FormInput
        label="Minimum Job Value"
        type="text"
        placeholder="e.g., $500"
        value={pp.job_requirements?.minimum_job_value || ""}
        onChange={(e) => updateJR("minimum_job_value", e.target.value)}
        icon={DollarSign}
      />
      <FormInput
        label="Deposit Required"
        type="text"
        placeholder="e.g., 20% upon acceptance"
        value={pp.job_requirements?.deposit_required || ""}
        onChange={(e) => updateJR("deposit_required", e.target.value)}
        icon={DollarSign}
      />

      <SubHeading title="8.3 Taxes & Payments" />
      <RadioGroup
        label="Tax Status"
        options={[
          { value: "", label: "Not set" },
          { value: "GST inclusive", label: "GST inclusive" },
          { value: "GST exclusive", label: "GST exclusive" },
          { value: "Tax-exempt", label: "Tax-exempt" },
        ]}
        value={pp.payment_and_finance?.tax_status || ""}
        onChange={(v) => updatePF("tax_status", v)}
        icon={FileText}
      />
      <StringListEditor
        label="Payment Methods"
        sublabel="Accepted forms of payment."
        values={paymentMethods}
        onChange={(next) => updatePF("payment_methods", next)}
        placeholder="e.g., Bank Transfer"
        icon={DollarSign}
      />

      <HiddenUi hidden={RENOVATION_UI_HIDDEN.financing_8_4}>
        <SubHeading title="8.4 Financing" />
        <ToggleSwitch
          label="Financing / payment plans offered"
          checked={!!pp.payment_and_finance?.financing_options?.offered}
          onChange={(v) => updateFin("offered", v)}
          icon={Scale}
        />
        {pp.payment_and_finance?.financing_options?.offered && (
          <FormInput
            label="Finance Provider Name"
            type="text"
            placeholder="e.g., Brighte Finance"
            value={pp.payment_and_finance?.financing_options?.provider_name || ""}
            onChange={(e) => updateFin("provider_name", e.target.value)}
            icon={Briefcase}
          />
        )}
      </HiddenUi>

      <HiddenUi hidden={RENOVATION_UI_HIDDEN.ballparkPricing_8_5}>
        <SubHeading title="8.5 Ballpark Pricing" />
        <ToggleSwitch
          label="Enforce 'No Exact Prices' policy"
          description="Never quote exact dollar amounts — always redirect to an on-site inspection."
          checked={!!pp.ballpark_pricing?.enforce_no_exact_prices}
          onChange={(v) => updateBP("enforce_no_exact_prices", v)}
          icon={ShieldCheck}
        />
        <p className="text-[#9CA3AF] text-xs font-body mt-2">
          Define price ranges per material / product. The Digital Brain will share these ranges only.
        </p>
        <div className="space-y-3">
          {products.map((p, idx) => (
            <div key={idx} className="p-4 rounded-lg bg-white/5 border border-white/10 space-y-3">
              <div className="flex items-center justify-between">
                <span className="font-mono text-[#00D1FF] text-xs">Product {idx + 1}</span>
                <button
                  type="button"
                  onClick={() => setProducts(products.filter((_, i) => i !== idx))}
                  className="text-[#9CA3AF] hover:text-red-400 transition-colors p-1"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
              <input
                type="text"
                value={p.product_name || ""}
                onChange={(e) =>
                  setProducts(
                    products.map((it, i) =>
                      i === idx ? { ...it, product_name: e.target.value } : it
                    )
                  )
                }
                placeholder="Product name (e.g., asphalt_shingles)"
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-[#F3F4F6] placeholder-[#9CA3AF]/50 focus:outline-none focus:border-[#00D1FF]/50 text-sm font-body"
              />
              <input
                type="text"
                value={p.pricing || ""}
                onChange={(e) =>
                  setProducts(
                    products.map((it, i) => (i === idx ? { ...it, pricing: e.target.value } : it))
                  )
                }
                placeholder="Pricing (e.g., $400 - $600 per square)"
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-[#F3F4F6] placeholder-[#9CA3AF]/50 focus:outline-none focus:border-[#00D1FF]/50 text-sm font-body"
              />
            </div>
          ))}
        </div>
        <button
          type="button"
          onClick={() => setProducts([...products, { product_name: "", pricing: "" }])}
          className="flex items-center gap-2 text-[#00D1FF] text-sm font-body hover:text-[#00D1FF]/80 transition-colors py-2"
        >
          <Plus className="w-4 h-4" />
          Add Product
        </button>
      </HiddenUi>
    </div>
  );
};

// ------------------------------------------------------------
// Step 9: Brand Voice & Communication
// ------------------------------------------------------------
const Step10Tone = ({ formData, setFormData, errors }) => {
  const tov = formData.tone_of_voice || {};
  const update = (field, value) =>
    setFormData({ ...formData, tone_of_voice: { ...tov, [field]: value } });

  return (
    <div className="space-y-6" data-testid="renovation-step-9">
      <SectionHeading
        title="Brand Voice & Communication"
        description="The Digital Brain's personality and how it represents your brand."
      />

      <SubHeading title="9.1 Tone & Style" />
      <FormInput
        label="Digital Brain Name"
        type="text"
        placeholder="Jack"
        value={tov.bot_name || ""}
        onChange={(e) => update("bot_name", e.target.value)}
        icon={User}
      />
      <RadioGroup
        label="Communication Style"
        required
        options={CANONICAL_TONE_STYLES.map((style) => ({
          value: style,
          label: style.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
          description:
            style === "professional_friendly"
              ? "Warm, clear, and trustworthy (canonical default)."
              : undefined,
        }))}
        value={tov.style || "professional_friendly"}
        onChange={(v) => update("style", v)}
        error={errors?.style}
        icon={Palette}
      />
      <ToggleSwitch
        label="Use emojis in messages"
        checked={!!tov.use_emojis}
        onChange={(v) => update("use_emojis", v)}
        icon={Smile}
      />
      <ToggleSwitch
        label={`Use Aussie slang (e.g., "G'day, mate")`}
        checked={!!tov.use_aussie_slang}
        onChange={(v) => update("use_aussie_slang", v)}
        icon={MessageSquare}
      />

      <SubHeading title="9.2 AI Disclosure" />
      <ToggleSwitch
        label="Disclose AI status"
        description="Whether the Digital Brain should admit it is an AI when asked."
        checked={!!tov.disclose_ai_status}
        onChange={(v) => update("disclose_ai_status", v)}
        icon={Info}
      />

      <SubHeading title="9.3 Filters" />
      <StringListEditor
        label="Forbidden Phrases"
        sublabel="Legal protection — the Digital Brain will never say these."
        values={tov.forbidden_phrases || []}
        onChange={(next) => update("forbidden_phrases", next)}
        placeholder="e.g., guarantee"
        icon={Gavel}
      />
      <StringListEditor
        label="Preferred Phrases / Slogans"
        sublabel="Brand phrases the Digital Brain should weave into replies."
        values={tov.preferred_phrases || []}
        onChange={(next) => update("preferred_phrases", next)}
        placeholder="e.g., Quality craftsmanship with a 5-year warranty"
        icon={Sparkles}
      />
      <StringListEditor
        label="Allowed Brand Phrases"
        sublabel="Approved marketing phrases the Digital Brain may use verbatim."
        values={tov.allowed_brand_phrases || []}
        onChange={(next) => update("allowed_brand_phrases", next)}
        placeholder="Fixed-price guarantee"
        icon={Sparkles}
      />
      <FormInput
        label="Tagline"
        type="text"
        placeholder="e.g., Built to withstand anything"
        value={tov.tagline || ""}
        onChange={(e) => update("tagline", e.target.value)}
        icon={Sparkles}
      />

      <SubHeading title="9.4 Scrub & Emoji Policy" />
      <FormInput
        label="Max emojis per session"
        type="number"
        min={0}
        value={tov.emoji_policy?.max_per_session ?? 2}
        onChange={(e) =>
          update("emoji_policy", {
            ...(tov.emoji_policy || {}),
            max_per_session: e.target.value ? parseInt(e.target.value, 10) : 0,
          })
        }
        icon={Smile}
      />
      <StringListEditor
        label="Reach-out scrub replacements"
        sublabel="Substitutes when scrubbing 'reach out' / system-speak."
        values={tov.replacement_phrases?.reach_out_scrub || []}
        onChange={(next) =>
          update("replacement_phrases", {
            ...(tov.replacement_phrases || {}),
            reach_out_scrub: next,
          })
        }
        placeholder="Happy to keep going right here — what's the next step?"
        icon={MessageSquare}
      />
      <StringListEditor
        label="Scheduling CTA replacements"
        values={tov.replacement_phrases?.scheduling_cta || []}
        onChange={(next) =>
          update("replacement_phrases", {
            ...(tov.replacement_phrases || {}),
            scheduling_cta: next,
          })
        }
        placeholder="I can get that scheduled — which day suits you?"
        icon={Calendar}
      />
    </div>
  );
};

// ------------------------------------------------------------
// Step 10: Lead Routing & Escalation
// ------------------------------------------------------------
const Step11Handoff = ({ formData, setFormData }) => {
  const hs = formData.knowledge_base.handoff_settings || {};
  const primaryRole = (hs.roles && hs.roles[0]) || { type: "general" };

  const updateHS = (field, value) =>
    setFormData({
      ...formData,
      knowledge_base: {
        ...formData.knowledge_base,
        handoff_settings: { ...hs, [field]: value },
      },
    });
  const updatePrimaryRole = (field, value) =>
    updateHS("roles", [{ ...primaryRole, type: "general", [field]: value }]);
  const updateHM = (field, value) =>
    updateHS("handoff_messages", { ...(hs.handoff_messages || {}), [field]: value });

  return (
    <div className="space-y-6" data-testid="renovation-step-10">
      <SectionHeading
        title="Lead Routing & Escalation"
        description="Who the Digital Brain hands off to when situations get complex."
      />

      <HiddenUi hidden={RENOVATION_UI_HIDDEN.supportRoles_10_1}>
        <SubHeading title="10.1 Primary Handoff Contact" />
        <p className="text-[#9CA3AF] text-xs font-body -mt-2 mb-4">
          All escalations (human requests, emergencies, warranty, commercial) route to this person.
        </p>
        <div className="p-5 rounded-xl bg-white/5 border border-white/10 space-y-4">
          <FormInput
            label="Role Title"
            type="text"
            placeholder="e.g., Office Manager"
            value={primaryRole.role || ""}
            onChange={(e) => updatePrimaryRole("role", e.target.value)}
            icon={Briefcase}
          />
          <FormInput
            label="Person Name"
            type="text"
            placeholder="e.g., Sarah"
            value={primaryRole.name || ""}
            onChange={(e) => updatePrimaryRole("name", e.target.value)}
            icon={User}
          />
          <FormInput
            label="Phone Number"
            type="tel"
            placeholder="e.g., +61 2 4222 0001"
            value={primaryRole.phone || ""}
            onChange={(e) => updatePrimaryRole("phone", e.target.value)}
            icon={Phone}
          />
          <FormInput
            label="Email"
            type="email"
            placeholder="e.g., hello@company.com"
            value={primaryRole.email || ""}
            onChange={(e) => updatePrimaryRole("email", e.target.value)}
            icon={Mail}
          />
        </div>
      </HiddenUi>

      <SubHeading title="10.2 Emergency Safety Actions (Agent 13)" />
      <EmergencySafetyActionsEditor
        actions={formData.knowledge_base.emergency_safety_actions || {}}
        onChange={(next) =>
          setFormData({
            ...formData,
            knowledge_base: {
              ...formData.knowledge_base,
              emergency_safety_actions: next,
            },
          })
        }
      />

      <SubHeading title="10.3 Handoff Triggers" />
      <StringListEditor
        label="When should the Digital Brain escalate to a human?"
        sublabel="e.g., abuse, repeated manager requests, complex insurance claims."
        values={hs.handoff_triggers || []}
        onChange={(next) => updateHS("handoff_triggers", next)}
        placeholder="e.g., customer_complaint_or_angry"
        icon={AlertTriangle}
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FormInput
          label="Human request handoff threshold"
          sublabel="Escalate after this many explicit human-agent requests."
          type="number"
          min={1}
          value={hs.human_request_handoff_threshold ?? 3}
          onChange={(e) =>
            updateHS(
              "human_request_handoff_threshold",
              e.target.value ? parseInt(e.target.value, 10) : 3
            )
          }
          icon={User}
        />
        <FormInput
          label="Emergency SLA text"
          sublabel="On-call response time shown in emergency drafts."
          type="text"
          placeholder="15 minutes"
          value={hs.emergency_sla_text || ""}
          onChange={(e) => updateHS("emergency_sla_text", e.target.value)}
          icon={Clock}
        />
      </div>

      <SubHeading title="10.4 Handoff Messages" />
      <FormTextarea
        label="General Escalation Message"
        rows={2}
        placeholder="I'll have one of our human specialists take over..."
        value={hs.handoff_messages?.general_escalation || ""}
        onChange={(e) => updateHM("general_escalation", e.target.value)}
        icon={MessageSquare}
      />
      <FormTextarea
        label="Emergency Escalation Message"
        rows={2}
        placeholder="I'm routing you directly to our on-call technician..."
        value={hs.handoff_messages?.emergency_escalation || ""}
        onChange={(e) => updateHM("emergency_escalation", e.target.value)}
        icon={Siren}
      />
      <FormTextarea
        label="Generic Failure Message"
        rows={2}
        placeholder="I'm having trouble completing that — let me connect you with our team..."
        value={hs.handoff_messages?.generic_failure || ""}
        onChange={(e) => updateHM("generic_failure", e.target.value)}
        icon={MessageSquare}
      />
    </div>
  );
};

// ------------------------------------------------------------
// Step 11: Legal & Compliance
// ------------------------------------------------------------
const Step12Legal = ({ formData, setFormData }) => {
  const lc = formData.knowledge_base.legal_and_compliance || {};
  const update = (field, value) =>
    setFormData({
      ...formData,
      knowledge_base: {
        ...formData.knowledge_base,
        legal_and_compliance: { ...lc, [field]: value },
      },
    });

  return (
    <div className="space-y-6" data-testid="renovation-step-11">
      <SectionHeading
        title="Legal & Compliance"
        description="Legal references and regulatory obligations the Digital Brain must consider."
      />

      <SubHeading title="11.1 Privacy" />
      <FormInput
        label="Privacy Policy URL"
        type="url"
        placeholder="https://your-company.com/privacy"
        value={lc.privacy_policy_url || ""}
        onChange={(e) => update("privacy_policy_url", e.target.value)}
        icon={Globe}
      />

      <SubHeading title="11.2 State Regulations" />
      <FormTextarea
        label="State / Regional Regulations"
        sublabel="Legal obligations the Digital Brain must remember (cooling-off periods, licensing, etc.)"
        rows={4}
        placeholder="e.g., By state law, jobs over $5,000 include a 5-day cooling-off period."
        value={lc.state_regulations || ""}
        onChange={(e) => update("state_regulations", e.target.value)}
        icon={Gavel}
      />
    </div>
  );
};

// ------------------------------------------------------------
// Step 12: Competitive Strategy & Success
// ------------------------------------------------------------
const Step13Strategy = ({ formData, setFormData }) => {
  const kb = formData.knowledge_base;
  const objections = kb.objection_handling || [];
  const cs = kb.competitive_strategy || {};
  const dv = cs.design_visualisation || {
    offered: false,
    stage_name: "",
    short_description: "",
  };

  const setObjections = (next) =>
    setFormData({
      ...formData,
      knowledge_base: { ...kb, objection_handling: next },
    });
  const updateCS = (field, value) =>
    setFormData({
      ...formData,
      knowledge_base: {
        ...kb,
        competitive_strategy: { ...cs, [field]: value },
      },
    });
  const updateDV = (field, value) => {
    const next = { ...dv, [field]: value };
    if (field === "offered" && !value) {
      next.stage_name = "";
      next.short_description = "";
    }
    updateCS("design_visualisation", next);
  };

  return (
    <div className="space-y-6" data-testid="renovation-step-12">
      <SectionHeading
        title="Competitive Strategy & Success"
        description="How the Digital Brain positions you against competitors and handles objections."
      />

      <SubHeading title="12.1 Objection Handling" />
      <p className="text-[#9CA3AF] text-xs font-body">
        Cheat sheet for common customer concerns — the Digital Brain will reach for these replies.
      </p>
      <div className="space-y-3">
        {objections.map((o, idx) => (
          <div key={idx} className="p-4 rounded-lg bg-white/5 border border-white/10 space-y-3">
            <div className="flex items-center justify-between">
              <span className="font-mono text-[#00D1FF] text-xs">Objection {idx + 1}</span>
              <button
                type="button"
                onClick={() => setObjections(objections.filter((_, i) => i !== idx))}
                className="text-[#9CA3AF] hover:text-red-400 transition-colors p-1"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
            <input
              type="text"
              value={o.objection || ""}
              onChange={(e) =>
                setObjections(
                  objections.map((it, i) => (i === idx ? { ...it, objection: e.target.value } : it))
                )
              }
              placeholder="Customer concern (e.g., Price is too high / I found it cheaper)"
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-[#F3F4F6] placeholder-[#9CA3AF]/50 focus:outline-none focus:border-[#00D1FF]/50 text-sm font-body"
            />
            <textarea
              value={o.response || ""}
              onChange={(e) =>
                setObjections(
                  objections.map((it, i) => (i === idx ? { ...it, response: e.target.value } : it))
                )
              }
              rows={3}
              placeholder="Recommended reply..."
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-[#F3F4F6] placeholder-[#9CA3AF]/50 focus:outline-none focus:border-[#00D1FF]/50 text-sm font-body resize-y"
            />
          </div>
        ))}
      </div>
      <button
        type="button"
        onClick={() => setObjections([...objections, { objection: "", response: "" }])}
        className="flex items-center gap-2 text-[#00D1FF] text-sm font-body hover:text-[#00D1FF]/80 transition-colors py-2"
      >
        <Plus className="w-4 h-4" />
        Add Objection
      </button>

      <SubHeading title="12.2 Design Visualisation" />
      <ToggleSwitch
        label="Offer design / visualisation"
        description="When enabled, the Digital Brain may mention your design stage using the copy below."
        checked={!!dv.offered}
        onChange={(v) => updateDV("offered", v)}
        icon={Sparkles}
      />
      {dv.offered ? (
        <>
          <FormInput
            label="Design stage name"
            placeholder="e.g., Design review"
            value={dv.stage_name || ""}
            onChange={(e) => updateDV("stage_name", e.target.value)}
            icon={Sparkles}
          />
          <FormTextarea
            label="Design visualisation summary"
            sublabel="Short marketing line the Digital Brain uses when design is part of your USP."
            rows={2}
            placeholder="Optional design visuals before the quote is finalised"
            value={dv.short_description || ""}
            onChange={(e) => updateDV("short_description", e.target.value)}
            icon={Sparkles}
          />
        </>
      ) : null}

      <SubHeading title="12.3 Strategy" />
      <StringListEditor
        label="Competitors Often Mentioned"
        sublabel="Names customers sometimes compare you to."
        values={cs.competitors_often_mentioned || []}
        onChange={(next) => updateCS("competitors_often_mentioned", next)}
        placeholder="e.g., Budget Reno Co"
        icon={Target}
      />
      <FormTextarea
        label="Core Differentiator"
        sublabel="Your key advantage the Digital Brain should emphasize."
        rows={3}
        placeholder="Fixed-Price Guarantee, clear scope before work starts, in-house team..."
        value={cs.core_differentiator || ""}
        onChange={(e) => updateCS("core_differentiator", e.target.value)}
        icon={Sparkles}
      />
      <StringListEditor
        label="Unique Selling Points"
        sublabel="Bullet points the Digital Brain may cite on price or timing objections."
        values={cs.unique_selling_points || []}
        onChange={(next) => updateCS("unique_selling_points", next)}
        placeholder="e.g., Fixed-Price Guarantee — contract price is final"
        icon={Sparkles}
      />
      <FormTextarea
        label="Process Overview"
        sublabel="High-level customer journey (post-visit steps are human team responsibility)."
        rows={2}
        placeholder="Enquiry → Consultation → Quote → Delivery → Handover"
        value={cs.process_overview || ""}
        onChange={(e) => updateCS("process_overview", e.target.value)}
        icon={FileText}
      />
    </div>
  );
};

// ==================== MAIN COMPONENT ====================

// ==================== MAIN COMPONENT ====================

const RenovationBotSettings = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [hasExistingSettings, setHasExistingSettings] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const { isActive: ghlIntegrationActive } = useGhlIntegrationActive();

  const steps = [
    { shortTitle: "Company" },
    { shortTitle: "Area" },
    { shortTitle: "Services" },
    { shortTitle: "Appts" },
    { shortTitle: "Calendar" },
    { shortTitle: "Qualify" },
    { shortTitle: "Decline" },
    { shortTitle: "Pricing" },
    { shortTitle: "Voice" },
    { shortTitle: "Handoff" },
    { shortTitle: "Legal" },
    { shortTitle: "Strategy" },
  ];
  const totalSteps = steps.length;

  // ---- Load existing settings ----
  useEffect(() => {
    const loadExisting = async () => {
      const token = getClientAuthToken();
    if (!token) {
        setIsLoading(false);
        return;
      }
      try {
        const backendUrl = process.env.REACT_APP_BACKEND_URL;
        const res = await axios.get(`${backendUrl}/api/bot/settings`, {
          headers: getClientAuthHeaders(),
        });
        if (res.data?.has_settings && res.data.settings) {
          const s = res.data.settings;
          const isUniversalDoc =
            s.bot_type === "universal_v1" || s.bot_type === "renovation_v1";
          if (isUniversalDoc) {
            setHasExistingSettings(true);
            setFormData(
              normalizeRenovationSettings(s, RENOVATION_BOT_SETTINGS_DEFAULTS)
            );
          }
        }
      } catch (err) {
        console.error("Error loading bot settings:", err);
      } finally {
        setIsLoading(false);
      }
    };
    loadExisting();
  }, []);

  // ---- LocalStorage: load on first render if no DB data ----
  useEffect(() => {
    if (!hasExistingSettings && !isLoading) {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          setFormData(
            normalizeRenovationSettings(parsed, RENOVATION_BOT_SETTINGS_DEFAULTS)
          );
        } catch (e) {
          console.error("Error parsing saved renovation data:", e);
        }
      }
    }
  }, [hasExistingSettings, isLoading]);

  // ---- Autosave to LocalStorage ----
  const saveToLocalStorage = useCallback(() => {
    if (!hasExistingSettings || isEditMode) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(formData));
    }
  }, [formData, hasExistingSettings, isEditMode]);

  useEffect(() => {
    saveToLocalStorage();
  }, [saveToLocalStorage]);

  // ---- Validation per step ----
  const validateStep = (step) => {
    const newErrors = {};
    switch (step) {
      case 1:
        if (!formData.knowledge_base.company_name?.trim()) {
          newErrors.company_name = "Company name is required";
        }
        if (
          formData.knowledge_base.website_url &&
          !validateUrl(formData.knowledge_base.website_url)
        ) {
          newErrors.website_url = "Please enter a valid URL";
        }
        break;
      case 9:
        if (!formData.tone_of_voice.style) {
          newErrors.style = "Please select a communication style";
        }
        break;
      default:
        break;
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const buildSubmissionPayload = () =>
    normalizeRenovationSettings(formData, RENOVATION_BOT_SETTINGS_DEFAULTS);

  const saveSettingsToDb = async () => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    const headers = { "Content-Type": "application/json", ...getClientAuthHeaders() };

    try {
      const res = await axios.post(
        `${backendUrl}/api/bot/save_settings/renovation_bot`,
        buildSubmissionPayload(),
        { headers }
      );

      if (res.data && res.data.status === "ok") {
        localStorage.removeItem(STORAGE_KEY);
        setHasExistingSettings(true);
        return true;
      }

      setErrors({
        submit: formatApiError(
          res.data?.detail ?? res.data?.message,
          "Failed to save settings. Please try again."
        ),
      });
      return false;
    } catch (err) {
      console.error("Save error:", err);
      setErrors({
        submit: formatApiError(
          err.response?.data?.detail ?? err.response?.data?.message,
          "Network error. Please check your connection and try again."
        ),
      });
      return false;
    }
  };

  const handleNext = async () => {
    if (!validateStep(currentStep)) return;
    if (currentStep >= totalSteps) return;

    setIsSaving(true);
    setErrors((prev) => {
      const next = { ...prev };
      delete next.submit;
      return next;
    });

    const ok = await saveSettingsToDb();
    setIsSaving(false);

    if (!ok) return;

    setCurrentStep(currentStep + 1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      setErrors({});
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handleGoToStep = (stepNum) => {
    if (stepNum === currentStep || isSaving) return;
    if (stepNum < 1 || stepNum > totalSteps) return;
    if (stepNum > currentStep && !hasExistingSettings) return;
    setCurrentStep(stepNum);
    setErrors({});
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Normalize various API error shapes (string, object, FastAPI 422 array) into a single string
  const formatApiError = (raw, fallback = "Failed to save settings. Please try again.") => {
    if (!raw) return fallback;
    if (typeof raw === "string") return raw;
    if (Array.isArray(raw)) {
      // FastAPI/Pydantic validation error array: [{ type, loc, msg, input, url }, ...]
      return (
        raw
          .map((e) => {
            if (typeof e === "string") return e;
            const path = Array.isArray(e?.loc)
              ? e.loc.filter((p) => p !== "body").join(".")
              : "";
            const message = e?.msg || e?.message || JSON.stringify(e);
            return path ? `${path}: ${message}` : message;
          })
          .join("; ") || fallback
      );
    }
    if (typeof raw === "object") {
      if (raw.msg) return raw.msg;
      if (raw.message) return raw.message;
      if (raw.detail) return formatApiError(raw.detail, fallback);
      try {
        return JSON.stringify(raw);
      } catch {
        return fallback;
      }
    }
    return String(raw);
  };

  const handleSubmit = async () => {
    if (!validateStep(currentStep)) return;

    setIsSaving(true);
    setErrors((prev) => {
      const next = { ...prev };
      delete next.submit;
      return next;
    });

    const ok = await saveSettingsToDb();
    setIsSaving(false);

    if (!ok) return;

    trackSettingsCompletedIfAuthenticated().catch((err) =>
      console.warn("[leadEvents] is_settings_completed failed", err),
    );

    setIsSubmitted(true);
    setIsEditMode(false);
  };

  const handleEditSettings = () => {
    setIsEditMode(true);
    setIsSubmitted(false);
    setCurrentStep(1);
  };

  const isNextDisabled = () => {
    switch (currentStep) {
      case 1:
        return !formData.knowledge_base.company_name?.trim();
      case 9:
        return !formData.tone_of_voice.style;
      default:
        return false;
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return <Step1Company formData={formData} setFormData={setFormData} errors={errors} />;
      case 2:
        return <Step2ServiceArea formData={formData} setFormData={setFormData} />;
      case 3:
        return <Step3Services formData={formData} setFormData={setFormData} />;
      case 4:
        return <Step4Appointments formData={formData} setFormData={setFormData} ghlIntegrationActive={ghlIntegrationActive} />;
      case 5:
        return <Step5Calendar formData={formData} setFormData={setFormData} />;
      case 6:
        return <Step7Qualification formData={formData} setFormData={setFormData} />;
      case 7:
        return <Step8Disqualifiers formData={formData} setFormData={setFormData} />;
      case 8:
        return <Step9Pricing formData={formData} setFormData={setFormData} />;
      case 9:
        return <Step10Tone formData={formData} setFormData={setFormData} errors={errors} />;
      case 10:
        return <Step11Handoff formData={formData} setFormData={setFormData} />;
      case 11:
        return <Step12Legal formData={formData} setFormData={setFormData} />;
      case 12:
        return <Step13Strategy formData={formData} setFormData={setFormData} />;
      default:
        return null;
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#0F1115] flex">
        <Sidebar />
        <div className="flex-1 md:ml-64 flex flex-col min-h-screen">
          <div className="flex-1 flex items-center justify-center px-6">
            <div className="text-center">
              <Loader2 className="w-12 h-12 text-[#00D1FF] animate-spin mx-auto mb-4" />
              <p className="text-[#9CA3AF] font-body">Loading your settings...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Configured state
  if ((hasExistingSettings && !isEditMode) || isSubmitted) {
    return (
      <div className="min-h-screen bg-[#0F1115] flex">
        <Sidebar />
        <div className="flex-1 md:ml-64 flex flex-col min-h-screen">
          <div className="flex-1 flex items-center justify-center px-6 py-12">
          <div className="max-w-lg w-full text-center">
            <div className="w-24 h-24 rounded-full bg-[#00D1FF]/20 flex items-center justify-center mx-auto mb-8 animate-pulse">
              <Sparkles className="w-12 h-12 text-[#00D1FF]" />
            </div>
            <h1 className="font-heading font-bold text-3xl md:text-4xl text-[#F3F4F6] mb-4">
              Settings Received!
            </h1>
            <p className="text-[#9CA3AF] font-body text-lg mb-6">
              Your Digital Brain settings have been saved and will be used to train the assistant.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={handleEditSettings}
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg border border-white/20 text-[#F3F4F6] font-heading font-semibold hover:bg-white/5 transition-colors"
              >
                <Pencil className="w-5 h-5" />
                Edit Settings
              </button>
            </div>
          </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0F1115] flex" data-testid="renovation-bot-settings-page">
      <Sidebar />

      <div className="flex-1 md:ml-64">
        <main className="pt-8 pb-32 px-6">
          <div className="max-w-2xl mx-auto">
            <StepIndicator
              steps={steps}
              currentStep={currentStep}
              onStepClick={handleGoToStep}
              hasExistingSettings={hasExistingSettings}
              isSaving={isSaving}
            />

            <div className="mb-10">
              <ProgressBar currentStep={currentStep} totalSteps={totalSteps} />
            </div>

            <div className="glass rounded-2xl p-6 md:p-10 border border-white/10">
              {renderStep()}
            </div>
          </div>
        </main>

        <div className="fixed bottom-0 left-0 md:left-64 right-0 bg-[#0F1115]/95 backdrop-blur-lg border-t border-white/5 p-4 md:p-6">
          <div className="max-w-2xl mx-auto">
            {errors.submit && (
              <div className="mb-4 p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm font-body text-center">
                {errors.submit}
              </div>
            )}

            <div className="flex items-center justify-between gap-4">
              {currentStep > 1 ? (
                <button
                  onClick={handleBack}
                  disabled={isSaving}
                  className="flex items-center gap-2 px-6 py-3 rounded-lg border border-white/20 text-[#F3F4F6] font-heading font-semibold hover:bg-white/5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="renovation-back-button"
                >
                  <ArrowLeft className="w-5 h-5" />
                  Back
                </button>
              ) : (
                <div />
              )}

              {currentStep < totalSteps ? (
                <button
                  onClick={handleNext}
                  disabled={isNextDisabled() || isSaving}
                  className={`flex items-center gap-2 px-8 py-3 rounded-lg font-heading font-bold transition-all ${
                    isNextDisabled() || isSaving
                      ? "bg-[#00D1FF]/30 text-[#0F1115]/50 cursor-not-allowed"
                      : "btn-pulse bg-[#00D1FF] text-[#0F1115] hover:bg-[#00D1FF]/90"
                  }`}
                  data-testid="renovation-next-button"
                >
                  {isSaving ? (
                    <>
                      <div className="w-5 h-5 border-2 border-[#0F1115]/30 border-t-[#0F1115] rounded-full animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      Next
                      <ArrowRight className="w-5 h-5" />
                    </>
                  )}
                </button>
              ) : (
                <button
                  onClick={handleSubmit}
                  disabled={isNextDisabled() || isSaving}
                  className={`flex items-center gap-2 px-8 py-3 rounded-lg font-heading font-bold transition-all ${
                    isNextDisabled() || isSaving
                      ? "bg-[#00D1FF]/30 text-[#0F1115]/50 cursor-not-allowed"
                      : "btn-pulse bg-[#00D1FF] text-[#0F1115] hover:bg-[#00D1FF]/90"
                  }`}
                  data-testid="renovation-submit-button"
                >
                  {isSaving ? (
                    <>
                      <div className="w-5 h-5 border-2 border-[#0F1115]/30 border-t-[#0F1115] rounded-full animate-spin" />
                      Launching...
                    </>
                  ) : (
                    <>
                      <Rocket className="w-5 h-5" />
                      Launch My Digital Brain
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export const UniversalBotSettings = RenovationBotSettings;
export default RenovationBotSettings;
